/**
 * TeamDeleteRowStatus: what a team's row says while its delete runs in
 * the background or after it failed (TRA-1508), on the owner's Teams list,
 * the staff Teams list and the team's Delete page. "Deleting… <step>"
 * while the api works; "Delete failed: <reason>" with a Retry button
 * after a failure, and the last Retry's own failure beside the reason.
 * Idle rows render nothing here.
 */

import { Button } from '@/components/ui/button'

import { deleteFailedText, type TeamDeleteRowState } from './teamDeletions'

interface TeamDeleteRowStatusProps {
  deletion: TeamDeleteRowState
  /** Null when the viewer may not retry (no rights to delete). */
  onRetry: ((deletionId: string) => void) | null
}

export function TeamDeleteRowStatus({
  deletion,
  onRetry,
}: TeamDeleteRowStatusProps): React.JSX.Element {
  if (deletion.kind === 'idle') return <></>
  if (deletion.kind === 'deleting') {
    return (
      <span className="shrink-0 text-xs text-muted-foreground" role="status">
        {deletion.label}
      </span>
    )
  }
  return (
    <span className="flex min-w-0 shrink items-center gap-2" role="alert">
      <span className="min-w-0 break-words text-xs text-destructive">
        {deleteFailedText(deletion.reason)}
        {deletion.retryError !== null && ` ${deletion.retryError}`}
      </span>
      {onRetry !== null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onRetry(deletion.deletionId)
          }}
        >
          Retry
        </Button>
      )}
    </span>
  )
}
